import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import BrandMark from "./BrandMark";
import { baht, formatDate } from "./ui";
import { TAX_DOC_TYPES } from "../services/taxDocument";
import { bahtText, branchLabel } from "../utils/thaiTax";

/**
 * พิมพ์ผ่าน window.print() — เรนเดอร์ children ลง portal (.print-area) แล้วสั่งพิมพ์
 * CSS @media print ซ่อน #root + โมดัลทั้งหมด เหลือแต่ .print-area จึงได้กระดาษเอกสารล้วน ๆ
 * token = ตัวเลขเปลี่ยนทุกครั้งที่กดพิมพ์ (0 = ไม่พิมพ์) · onDone เรียกเมื่อปิดหน้าต่างพิมพ์
 */
export function PrintArea({ token, onDone, children }) {
  useEffect(() => {
    if (!token) return undefined;
    const done = () => onDone?.();
    window.addEventListener("afterprint", done);
    const timer = setTimeout(() => window.print(), 80);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("afterprint", done);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!token) return null;
  return createPortal(<div className="print-area">{children}</div>, document.body);
}

const COPY_LABEL = { original: "ต้นฉบับ", copy: "สำเนา" };

/**
 * กระดาษเอกสารภาษี — ใช้ทั้งพรีวิวและพิมพ์
 *   doc   = แถว tax_documents
 *   brand = brandingOf(organization) (โลโก้/ชื่อแบรนด์ของร้านถ้ามีสิทธิ์ White-label)
 *   copy  = "original" | "copy" | "" (ไม่ใส่ป้าย)
 */
export default function TaxDocumentPaper({ doc, brand, copy = "" }) {
  if (!doc) return null;
  const type = TAX_DOC_TYPES[doc.docType] || { label: doc.docType };
  const seller = doc.seller || {};
  const buyer = doc.buyer || {};
  const isCredit = doc.docType === "credit_note";
  const cancelled = doc.status === "cancelled";
  const draft = doc.status === "draft";
  const inclusive = doc.priceMode === "inclusive";

  return (
    <div className="tax-paper" style={brand?.cssVars}>
      {cancelled ? <div className="tax-paper__watermark">ยกเลิก</div> : null}
      {draft ? <div className="tax-paper__watermark is-draft">ฉบับร่าง</div> : null}

      <div className="tax-paper__head">
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          {brand?.custom ? <BrandMark brand={brand} size={48} /> : null}
          <div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>{seller.name || brand?.name}</div>
            <div>{seller.address}</div>
            <div>
              เลขประจำตัวผู้เสียภาษี {seller.taxId || "-"} ({branchLabel(seller.branchCode)}
              {seller.branchName ? ` ${seller.branchName}` : ""})
            </div>
            {seller.phone ? <div>โทร {seller.phone}</div> : null}
          </div>
        </div>
        <div>
          <div className="tax-paper__title">{type.label}</div>
          <div style={{ textAlign: "right" }}>
            {copy ? <span className="tax-paper__copy">{COPY_LABEL[copy]}</span> : null}
          </div>
          <div style={{ textAlign: "right", marginTop: 6 }}>
            <div>เลขที่ <strong>{doc.docNo || "(ยังไม่ออกเลขที่)"}</strong></div>
            <div>วันที่ {formatDate(doc.issuedAt || doc.createdAt)}</div>
          </div>
        </div>
      </div>

      <div className="tax-paper__parties">
        <div className="tax-paper__box">
          <h4>ผู้ซื้อ / ลูกค้า</h4>
          <div><strong>{buyer.name || "-"}</strong></div>
          <div>{buyer.address || "-"}</div>
          <div>
            เลขประจำตัวผู้เสียภาษี {buyer.taxId || "-"}
            {buyer.taxId ? ` (${branchLabel(buyer.branchCode)})` : ""}
          </div>
        </div>
        <div className="tax-paper__box">
          <h4>{isCredit ? "อ้างอิงเอกสารเดิม" : "อ้างอิง"}</h4>
          {isCredit ? (
            <>
              <div>{TAX_DOC_TYPES[doc.refDocType]?.label || "ใบกำกับภาษี"} เลขที่ <strong>{doc.refDocNo}</strong></div>
              <div>ยอดเดิม {baht(doc.refDocTotal)}</div>
              <div>เหตุผล: {doc.reason}</div>
            </>
          ) : (
            <>
              {doc.invoiceNo ? <div>ใบแจ้งหนี้ {doc.invoiceNo}</div> : null}
              {doc.receiptNo ? <div>ใบเสร็จรับเงิน {doc.receiptNo}</div> : null}
              {!doc.invoiceNo && !doc.receiptNo ? <div>-</div> : null}
            </>
          )}
          {doc.note ? <div>หมายเหตุ: {doc.note}</div> : null}
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th style={{ width: 36 }}>#</th>
            <th>รายการ</th>
            <th className="is-num">จำนวน</th>
            <th className="is-num">ราคา/หน่วย</th>
            <th className="is-num">จำนวนเงิน</th>
          </tr>
        </thead>
        <tbody>
          {(doc.items || []).map((item, index) => (
            <tr key={index}>
              <td>{index + 1}</td>
              <td>{item.name}</td>
              <td className="is-num">{item.quantity}</td>
              <td className="is-num">{baht(item.price)}</td>
              <td className="is-num">{baht(item.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="tax-paper__sum">
        <div><span>รวมเป็นเงิน{inclusive ? " (รวม VAT)" : ""}</span><span>{baht(doc.subtotal)}</span></div>
        {Number(doc.discount) ? <div><span>ส่วนลด</span><span>-{baht(doc.discount)}</span></div> : null}
        <div><span>มูลค่าก่อนภาษี</span><span>{baht(doc.vatBase)}</span></div>
        <div><span>ภาษีมูลค่าเพิ่ม {doc.vatRate ?? 7}%</span><span>{baht(doc.vatAmount)}</span></div>
        <div className="is-grand"><span>{isCredit ? "ยอดลดหนี้รวม" : "จำนวนเงินรวมทั้งสิ้น"}</span><span>{baht(doc.total)}</span></div>
      </div>
      <div className="tax-paper__text">({bahtText(doc.total)})</div>

      {cancelled ? (
        <div className="tax-paper__notice" style={{ borderColor: "#dc2626", background: "#fef2f2", color: "#991b1b" }}>
          เอกสารนี้ถูกยกเลิก{doc.cancelledAt ? ` เมื่อ ${formatDate(doc.cancelledAt)}` : ""} · เหตุผล: {doc.cancelReason}
        </div>
      ) : null}
      {!cancelled && doc.status !== "pending_rd" ? (
        <div className="tax-paper__notice">
          {draft ? "ฉบับร่าง — ยังไม่ใช่เอกสารทางการและยังไม่มีเลขที่ · " : ""}
          เอกสารนี้ยังไม่ได้ส่งให้กรมสรรพากร (ระบบยังไม่ได้เชื่อมต่อผู้ให้บริการ e-Tax)
        </div>
      ) : null}

      <div className="tax-paper__foot">
        {brand && !brand.custom ? `ออกโดยระบบ ${brand.name}` : ""}
      </div>
    </div>
  );
}
